/**
 * Compiler adapter — PR6 cutover shape.
 *
 * Single production chokepoint that produces a `WorksheetQueryExecution` for
 * every worksheet execution. Before PR6 the adapter routed on backend id and
 * swallowed `WorksheetCompilerNotImplementedError` to re-dispatch through the
 * TS-legacy compiler. After PR6 the adapter routes *structurally*:
 *
 *   1. If the caller supplied a `typedSpec` and the Rust backend is
 *      installed → Rust compiles the typed input. Any error from Rust
 *      propagates; it is not a fallback trigger.
 *   2. Otherwise → TS-legacy only when strict switch mode is not
 *      `typed-required`. In `typed-required` mode, any attempt to compile
 *      without a typed spec, or with the TS backend active, is a hard
 *      cutover failure.
 *
 * Contract invariants (do not loosen without bumping
 * `WORKSHEET_COMPILER_CONTRACT_VERSION` plus the matching Rust
 * `CURRENT_CONTRACT_VERSION`):
 *   - The adapter is the only production producer of `WorksheetQueryExecution`
 *     for worksheet execution. The low-level `buildWorksheetQueryExecution`
 *     stays exported for test scaffolding only.
 *   - The adapter never silently swallows compiler errors. A crash in Rust
 *     surfaces; the adapter does not hide it behind ts-legacy.
 *   - Backend selection is a worker-boot decision, not a request-time one.
 *     `src/worker/worker.ts` installs the Rust backend unconditionally on
 *     init; the `use_ts_legacy_compiler` flag is a kill-switch, not the
 *     normal path.
 */
import type { WorksheetSpecV1 } from './spec'
import type { WorksheetQueryExecution } from './worksheetQueryArtifacts'
import type { WorksheetQueryOptions } from './worksheetQueryBuilder'
import type { PreparedWorksheetExecutionCore } from './worksheetExecutionCore'
import type { TypedWorksheetSpec } from './worksheetCompilerTyped'
import type { ResolvedWorksheetActionState } from '../dashboard/resolveWorksheetActionState'
import { buildWorksheetQueryExecution } from './worksheetQueryExecution'

/**
 * Wire-format version for the Rust worksheet compiler request/response.
 *
 * Must stay in lockstep with `CURRENT_CONTRACT_VERSION` in
 * `query_builder/src/worksheet_compiler/request.rs`. Bumping this constant
 * requires a matching Rust bump plus a `CacheManifestStore.contracts` purge
 * of `worksheet_compiler_contract_version` in the same commit: the worker's
 * `contract_check` stage drops any persisted datasource-execution-node
 * tables when the versions disagree (see `src/worker/worker.ts`).
 *
 * History:
 *   v1 — Phase 4: typed-spec compile path, planner identity, finalSqlHash.
 *   v2 — Phase 5a: Rust ports of datasource-node identity helpers and
 *        agg-base identity helpers. Any cached agg-base / node signature
 *        from a v1 compiler is considered stale and purged on first load.
 *   v3 — PR6 cutover: Rust backend default-on at worker boot; adapter no
 *        longer swallows `WorksheetCompilerNotImplementedError` to
 *        re-dispatch through ts-legacy. In production strict mode,
 *        every worksheet compile requires a lowered `typedSpec` and a
 *        Rust backend, so any TS-legacy dispatch attempt hard fails.
 *        Any cached datasource-execution-graph node signatures emitted
 *        under v2 are considered stale and are purged by the worker's
 *        `contract_check` stage on first boot.
 */
export const WORKSHEET_COMPILER_CONTRACT_VERSION = 3 as const

export type CompileWorksheetExecutionInput = {
  worksheetSpec: WorksheetSpecV1
  queryOptions: WorksheetQueryOptions
  emitExecPlan?: boolean
  /**
   * Optional. When provided, gives the adapter access to the full prepared
   * execution contract so the Rust backend can consume
   * `UnifiedWorksheetExecutionRequest` directly (Phase 4+).
   */
  preparedExecutionCore?: PreparedWorksheetExecutionCore | null
  /**
   * Phase 4+ hook: production callers must produce a `TypedWorksheetSpec`.
   * When absent under `typed-required` strictness, the adapter hard fails
   * before the TS legacy backend can run.
   *
   * This intentionally lives on the adapter input rather than inside
   * `queryOptions` so typed-spec drift cannot contaminate legacy cache
   * identities (which are derived from `queryOptions`).
   */
  typedSpec?: TypedWorksheetSpec | null
  /**
   * Phase 8 hook: request-time overrides for `typedSpec.parameters`.
   * Passed straight through to the Rust backend which validates the names
   * against declared parameters and inlines the resolved literals into
   * SQL. Safe to set even with the TS-legacy backend — it ignores the
   * field (legacy worksheets have no typed parameters).
   */
  boundParameters?: Record<string, unknown> | null
  /**
   * Compiler-rewrite PR3 — normalized runtime filter / dashboard-action state.
   *
   * Live callers that sit under a dashboard (extract-backed execution,
   * dashboard tiles, worker prelude) resolve the raw `ActionResult` through
   * `src/dashboard/resolveWorksheetActionState.ts` into a single
   * `ResolvedWorksheetActionState`. Before PR3 that resolved state only
   * reached the Rust compiler via `NormalizedWorksheetRequest.filters`,
   * which the Rust lowering pass stuffs into the semantic signature but
   * NOT into executed SQL (see
   * `query_builder/src/worksheet_compiler/lowering.rs`:
   *   `let semantic_source = json!({ ..., "filters": ..., ... });`).
   * That makes runtime filters signature-only, which is a correctness
   * trap the plan calls out in "issue 6".
   *
   * Carrying the already-resolved state on the adapter input is the
   * TS-side half of the fix: every production caller routes through one
   * `ResolvedWorksheetActionState` and the Rust filter-lowering PR can
   * consume it verbatim without re-deriving semantics. Today the field
   * is informational — the adapter does not mutate compile output from
   * it — but test fixtures and the upcoming Rust lowering PR can rely on
   * its presence.
   *
   * IMPORTANT: this field does NOT replace `NormalizedWorksheetRequest.filters`
   * in the existing `UnifiedWorksheetExecutionRequest`; those filters
   * continue to flow through request preparation. The resolved state here
   * is a strict superset (adds highlights, parameters, tuple filters, and
   * the explicit-empty flag) and is the single source of truth once Rust
   * lowering lands.
   */
  resolvedActionState?: ResolvedWorksheetActionState | null
  /**
   * Source tag for diagnostics when fail-fast assertions fire upstream.
   * Does NOT participate in any identity or signature computation.
   */
  callerTag?: string
  /**
   * Workstream A step 6/7 diagnostic surface: when
   * `buildWorksheetCompilerInput` attempts to lower the authored
   * worksheet into a `typedSpec` but rejects on an explicit gate, this
   * carries the stable reason tag (e.g. `measure_filter_not_yet_typed`,
   * `table_calcs_not_yet_typed`). The adapter itself never consumes
   * this field — it's purely for switch-readiness telemetry and for
   * the PR6 fail-fast assertion that every production input carries a
   * typedSpec. Null (not undefined) means the lowering returned a
   * typedSpec successfully.
   */
  typedSpecRejectionReason?: string | null
  /**
   * PR4 Workstream D step 1 seam (see
   * `docs/compiler-rewrite/presentation-gap-ownership.md` item 5):
   *
   * Paging decides which columns the UI needs for its current page.
   * This arrives on the compile input as an explicit projection-augmentation
   * requirement; the typed-spec lowering then validates every named field
   * is already projected (or, in a future phase, adds it). Rust projects
   * exactly the requested column set. TS never adds or strips columns
   * after compile — the post-compile column stripping currently hiding in
   * `localWorksheetExecution.ts` is deleted at PR6.
   *
   * `null` or `undefined` means "no paging projection requirement" — the
   * default today. An empty `fields: []` is treated the same as absent.
   */
  pagingProjection?: { fields: readonly string[] } | null
}

export type WorksheetCompilerBackendId = 'ts-legacy' | 'rust'

export type WorksheetCompilerBackend = {
  id: WorksheetCompilerBackendId
  compile(input: CompileWorksheetExecutionInput): WorksheetQueryExecution
}

const TS_LEGACY_BACKEND: WorksheetCompilerBackend = {
  id: 'ts-legacy',
  compile(input) {
    return buildWorksheetQueryExecution(input.worksheetSpec, {
      ...(input.queryOptions as any),
      emitExecPlan: Boolean(
        input.emitExecPlan ||
          (input.queryOptions as any)?.emitExecPlan ||
          (input.queryOptions as any)?.collectExecPlan ||
          (input.queryOptions as any)?.collect_exec_plan,
      ),
    } as any)
  },
}

let activeBackend: WorksheetCompilerBackend = TS_LEGACY_BACKEND

/**
 * Phase 4+ hook. The Rust bridge calls this during module init once the WASM
 * module is ready and feature parity for the current scope has been verified
 * by the conformance harness. Phase 2a ships with this intentionally unused.
 */
export function setWorksheetCompilerBackend(backend: WorksheetCompilerBackend): void {
  activeBackend = backend
}

export function resetWorksheetCompilerBackend(): void {
  activeBackend = TS_LEGACY_BACKEND
  strictSwitchMode = 'off'
}

export function getActiveWorksheetCompilerBackendId(): WorksheetCompilerBackendId {
  return activeBackend.id
}

/**
 * Compiler-rewrite Workstream A step 7 — fail-fast switch readiness mode.
 *
 * Three levels, graduated to match the plan's rollout order:
 *
 *   - `'off'` (default today): no enforcement; the adapter runs as-is.
 *   - `'caller-tag'`: every call to `compileWorksheetExecution` MUST
 *      carry a `callerTag` identifying which live path produced the
 *      input. This is the PR1 discipline: live paths cannot bypass the
 *      builder. Tests that construct raw inputs without a tag still
 *      fail fast.
 *   - `'typed-required'`: all of the above, plus every input MUST carry
 *      a non-null `typedSpec` and the active backend MUST be Rust. This
 *      is the cutover gate: any path that would dispatch through
 *      TS-legacy is a regression and crashes immediately.
 *
 * Intentionally `'off'` by default for unit-test scaffolding. The worker
 * enables `typed-required` after installing the Rust backend.
 */
export type WorksheetCompilerStrictSwitchMode =
  | 'off'
  | 'caller-tag'
  | 'typed-required'

let strictSwitchMode: WorksheetCompilerStrictSwitchMode = 'off'

export function setWorksheetCompilerStrictSwitchMode(
  mode: WorksheetCompilerStrictSwitchMode | boolean,
): void {
  if (typeof mode === 'boolean') {
    strictSwitchMode = mode ? 'caller-tag' : 'off'
    return
  }
  strictSwitchMode = mode
}

export function getWorksheetCompilerStrictSwitchMode(): WorksheetCompilerStrictSwitchMode {
  return strictSwitchMode
}

/**
 * The single adapter entry point. Every production call site in `src/`
 * routes through here; `buildWorksheetQueryExecution` is a lower-level
 * helper retained for the ts-legacy backend and test scaffolding.
 *
 * Routing rule (PR6 cutover shape):
 *
 *   - typedSpec present AND active backend is Rust → Rust compiles.
 *     Any error from Rust propagates verbatim; there is no silent
 *     re-dispatch. If Rust crashes on a typed spec, that's a real
 *     drift that must be fixed at the source, not hidden.
 *   - typedSpec absent (lowering rejected) OR active backend is
 *     ts-legacy → ts-legacy compiles only outside `typed-required`
 *     mode. In `typed-required`, either condition is a hard failure.
 *
 * The pre-PR6 `WorksheetCompilerNotImplementedError` catch-and-retry
 * branch is deliberately removed. Its behavior was semantically
 * identical to "route ts-legacy when typedSpec is missing" but
 * implemented by exception, which meant any unrelated Rust crash
 * whose `name` happened to match would be silently masked.
 */
export function compileWorksheetExecution(
  input: CompileWorksheetExecutionInput,
): WorksheetQueryExecution {
  if (strictSwitchMode !== 'off' && !input.callerTag) {
    throw new Error(
      'compileWorksheetExecution called without `callerTag` while strict switch mode is ' +
        `'${strictSwitchMode}'. Every live compile path must construct its adapter input ` +
        'via a factory in src/tableau/buildWorksheetCompilerInput.ts so `callerTag` is ' +
        'populated.',
    )
  }
  if (strictSwitchMode === 'typed-required' && !input.typedSpec) {
    throw new Error(
      'compileWorksheetExecution called without `typedSpec` while strict switch mode is ' +
        "'typed-required'. After the PR6 cutover the Rust backend is the only live path, " +
        'so every input must carry a lowered TypedWorksheetSpec from ' +
        'tryBuildTypedSpecFromAuthoring. Rejection reason: ' +
        `${input.typedSpecRejectionReason ?? 'unknown'} (callerTag=${input.callerTag}).`,
    )
  }
  if (strictSwitchMode === 'typed-required' && activeBackend.id !== 'rust') {
    throw new Error(
      'compileWorksheetExecution would dispatch through TS legacy while strict switch mode is ' +
        "'typed-required'. The Rust worksheet compiler backend must be installed before live " +
        `worksheet compilation (activeBackend=${activeBackend.id}, callerTag=${input.callerTag ?? 'unknown'}).`,
    )
  }
  // PR6 structural routing: Rust only compiles typed inputs. Outside
  // production strictness, the TS legacy backend remains available for
  // tests and direct scaffolding; `typed-required` turns this branch into
  // a hard failure before dispatch.
  if (activeBackend.id === 'rust' && input.typedSpec) {
    return activeBackend.compile(input)
  }
  return TS_LEGACY_BACKEND.compile(input)
}
